import { useState, useEffect } from 'react';
import { normalizeQuoteEventDetail, groupQuotesByOrderId, mergeQuotesIntoOrders } from "./RfqComponents/useWebSocketQuoteUpdates";
import { isStagingOrder, isLiveOrder } from "../../Constants/stagingBlotter";
import { updateOrdersBlotter } from "../../Utils/ordersBlotterUpdates";

// Utility function to check if an order belongs in the staging blotter.
// Delegates to the single source of truth in Constants/stagingBlotter.js.
export const isOrderForStagingBlotter = (order) => isStagingOrder(order);

// Utility function to check if an order belongs in the regular orders.
// Delegates to the single source of truth in Constants/stagingBlotter.js.
export const isOrderForRegularBlotter = (order) => isLiveOrder(order);

// Maintain a maximum number of items in the array based on rowsPerPage
const limitArraySize = (array, maxSize) => {
  if (array.length > maxSize) {
    return array.slice(0, maxSize);
  }
  return array;
};

// Utility to consistently grab an order's identifier
const getOrderId = (order) => order?.orderId ?? order?.id;

const handleOrdersBlotterUpdate = (orders, update, setTotalElements, rowsPerPage, remove = false) => {
  const result = updateOrdersBlotter(orders, update, rowsPerPage, remove);
  if (result.totalDelta) {
    setTimeout(() => setTotalElements((total) => Math.max(0, total + result.totalDelta)), 0);
  }
  return result.orders;
};

// Handle ladder order updates in staging blotter
const handleLadderOrderUpdate = (prevOrders, updatedOrder, setTotalStagingElements, rowsPerPage) => {
  const groupIdx = prevOrders.findIndex(
    o => o.bondLadderId === updatedOrder.bondLadderId && Array.isArray(o.ladderOrders)
  );
  let updatedGroups = [...prevOrders];
  const targetId = getOrderId(updatedOrder);

  if (groupIdx !== -1) {
    // Group exists
    const group = { ...updatedGroups[groupIdx] };
    const orderIdx = group.ladderOrders.findIndex(
      o => getOrderId(o) === targetId
    );
    if (orderIdx !== -1) {
      // Partial merge: incoming fields overwrite, rest stay
      group.ladderOrders[orderIdx] = { ...group.ladderOrders[orderIdx], ...updatedOrder };
    } else {
      // Add new ladder order
      group.ladderOrders.unshift(updatedOrder);
      setTimeout(() => {
        setTotalStagingElements(prev => prev + 1);
      }, 0.5);
    }
    updatedGroups[groupIdx] = group;
  } else {
    // No group, create new
    updatedGroups.unshift({
      bondLadderId: updatedOrder.bondLadderId,
      // Carried so the synthesized group matches the shape the REST fetch produces, which
      // spreads a real parent order. Without it a ladder-linked characteristic group looks
      // like a plain ladder until the next refresh.
      characteristicId: updatedOrder.characteristicId,
      ladderOrders: [updatedOrder],
      ladderCharacteristics: updatedOrder.ladderCharacteristics,
      nameOfTheLadder: updatedOrder.nameOfTheLadder,
      isLadderGroup: true,
    });
    setTimeout(() => {
      setTotalStagingElements(prev => prev + 1);
    }, 0.5);
  }
  return limitArraySize(updatedGroups, rowsPerPage);
};

// Handle regular (non-ladder) order updates
const handleRegularOrderUpdate = (prevOrders, updatedOrder, setTotalElements, rowsPerPage) => {
  const targetId = getOrderId(updatedOrder);
  const existingOrderIndex = prevOrders.findIndex(
    order => getOrderId(order) === targetId
  );
  let updatedOrders;
  if (existingOrderIndex !== -1) {
    updatedOrders = [...prevOrders];
    // Partial merge: incoming fields overwrite existing, others persist
    updatedOrders[existingOrderIndex] = { ...updatedOrders[existingOrderIndex], ...updatedOrder };
  } else {
    updatedOrders = [updatedOrder, ...prevOrders];
    setTimeout(() => {
      setTotalElements(prev => prev + 1);
    }, 0.5);
  }
  return limitArraySize(updatedOrders, rowsPerPage);
};

// Remove ladder order from staging blotter
const removeLadderOrderFromStaging = (prevOrders, updatedOrder, setTotalStagingElements) => {
  const targetId = getOrderId(updatedOrder);
  const groupIdx = prevOrders.findIndex(
    o => o.bondLadderId === updatedOrder.bondLadderId && Array.isArray(o.ladderOrders)
  );
  if (groupIdx !== -1) {
    const group = { ...prevOrders[groupIdx] };
    const orderIdx = group.ladderOrders.findIndex(
      o => getOrderId(o) === targetId
    );
    if (orderIdx !== -1) {
      group.ladderOrders.splice(orderIdx, 1);
      setTimeout(() => {
        setTotalStagingElements(prev => Math.max(0, prev - 1));
      }, 0.5);
    }
    // If no orders left, remove group
    let updatedGroups = [...prevOrders];
    if (group.ladderOrders.length === 0) {
      updatedGroups.splice(groupIdx, 1);
    } else {
      updatedGroups[groupIdx] = group;
    }
    return updatedGroups;
  }
  return prevOrders;
};

// Remove regular order from any blotter
const removeRegularOrder = (prevOrders, orderOrId, setTotalElements) => {
  const targetId = getOrderId(orderOrId) ?? orderOrId;
  const existingIndex = prevOrders.findIndex(
    order => getOrderId(order) === targetId
  );
  if (existingIndex !== -1) {
    setTimeout(() => {
      setTotalElements(prev => Math.max(0, prev - 1));
    }, 0.5);
    return prevOrders.filter(order => getOrderId(order) !== targetId);
  }
  return prevOrders;
};

// Remove order based on isActive status from any blotter (staging or regular)
const removeInactiveOrder = (prevOrders, orderOrId, setTotalElements) => {
  const targetId = getOrderId(orderOrId) ?? orderOrId;
  // Check if it's in a ladder group
  const groupIdx = prevOrders.findIndex(
    group => group.isLadderGroup && Array.isArray(group.ladderOrders) &&
    group.ladderOrders.some(order => getOrderId(order) === targetId)
  );
  
  if (groupIdx !== -1) {
    // Remove from ladder group
    const group = { ...prevOrders[groupIdx] };
    const orderIdx = group.ladderOrders.findIndex(order => getOrderId(order) === targetId);
    if (orderIdx !== -1) {
      group.ladderOrders.splice(orderIdx, 1);
      setTimeout(() => {
        setTotalElements(prev => Math.max(0, prev - 1));
      }, 0.5);
    }
    
    let updatedGroups = [...prevOrders];
    if (group.ladderOrders.length === 0) {
      // Remove empty group
      updatedGroups.splice(groupIdx, 1);
    } else {
      updatedGroups[groupIdx] = group;
    }
    return updatedGroups;
  } else {
    // Remove regular order
    return removeRegularOrder(prevOrders, targetId, setTotalElements);
  }
};

// Update quotes for orders in any blotter
const updateOrderQuotes = (prevOrders, orderOrGrouped, quotesMaybe) => {
  if (!Array.isArray(prevOrders) || !prevOrders.length) return prevOrders;

  let groupedQuotes = null;
  if (orderOrGrouped && typeof orderOrGrouped === "object" && !Array.isArray(orderOrGrouped)) {
    groupedQuotes = orderOrGrouped;
  } else if (orderOrGrouped != null && Array.isArray(quotesMaybe)) {
    groupedQuotes = { [orderOrGrouped]: quotesMaybe };
  } else if (Array.isArray(orderOrGrouped)) {
    groupedQuotes = groupQuotesByOrderId(orderOrGrouped);
  }

  if (!groupedQuotes || !Object.keys(groupedQuotes).length) return prevOrders;
  return mergeQuotesIntoOrders(prevOrders, groupedQuotes);
};

// Custom hook to handle WebSocket updates with venue-based filtering
export const useWebSocketOrderUpdates = (setStagingOrderData, setOrderData, setTotalStagingElements, setTotalOrderElements, rowsPerPage) => {
  useEffect(() => {
    const handleWebSocketUpdate = (event) => {
      const updatedOrder = event.detail;
      const targetId = getOrderId(updatedOrder);

      // Check if order is inactive and should be removed
      if (updatedOrder.isActive === 0) {
        // Remove from staging blotter
        setStagingOrderData(prevOrders => 
          removeInactiveOrder(prevOrders, targetId, setTotalStagingElements)
        );
        
        // Remove from regular orders blotter
        setOrderData(prevOrders => 
          handleOrdersBlotterUpdate(prevOrders, updatedOrder, setTotalOrderElements, rowsPerPage, true)
        );
        
        return; // Exit early, no further processing needed
      }
      
      const isForStagingBlotter = isOrderForStagingBlotter(updatedOrder);

      if (isForStagingBlotter) {
        setStagingOrderData(prevOrders => {
          // Ladder order case
          if (updatedOrder.bondLadderId) {
            return handleLadderOrderUpdate(prevOrders, updatedOrder, setTotalStagingElements, rowsPerPage);
          }
          // Regular staging order
          return handleRegularOrderUpdate(prevOrders, updatedOrder, setTotalStagingElements, rowsPerPage);
        });

        // Remove from regular orders if it exists there
        setOrderData(prevOrders => 
          handleOrdersBlotterUpdate(prevOrders, updatedOrder, setTotalOrderElements, rowsPerPage, true)
        );
      } else {
        // Remove from staging if present
        setStagingOrderData(prevOrders => {
          if (updatedOrder.bondLadderId) {
            return removeLadderOrderFromStaging(prevOrders, updatedOrder, setTotalStagingElements);
          }
          return removeRegularOrder(prevOrders, targetId, setTotalStagingElements);
        });

        // Add/update in regular orders
        setOrderData(prevOrders => 
          handleOrdersBlotterUpdate(prevOrders, updatedOrder, setTotalOrderElements, rowsPerPage)
        );
      }
    };

    const handleQuoteUpdates = (event) => {
      const normalizedQuotes = normalizeQuoteEventDetail(event.detail);
      if (!normalizedQuotes.length) return;

      const groupedQuotes = groupQuotesByOrderId(normalizedQuotes);
      if (!Object.keys(groupedQuotes).length) return;

      setOrderData(prevOrders => updateOrderQuotes(prevOrders, groupedQuotes));
      setStagingOrderData(prevOrders => updateOrderQuotes(prevOrders, groupedQuotes));
    };

    // Add event listeners 
    window.addEventListener("", handleWebSocketUpdate);
    window.addEventListener("orderUpdate", handleWebSocketUpdate);
    window.addEventListener("orderQuotesUpdate", handleQuoteUpdates);
    
    return () => {
      window.removeEventListener("", handleWebSocketUpdate);
      window.removeEventListener("orderUpdate", handleWebSocketUpdate);
      window.removeEventListener("orderQuotesUpdate", handleQuoteUpdates);
    };
  }, [setStagingOrderData, setOrderData, setTotalStagingElements, setTotalOrderElements, rowsPerPage]);
};
